import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import AudioClipLibrary from "@/components/audio/AudioClipLibrary";
import AudioPreviewButton from "@/components/audio/AudioPreviewButton";
import { useGetAudioClipsQuery } from "@/store/rest_api";
import { FaMusic } from "react-icons/fa";

interface WaitAudioPickerProps {
  value?: string | null;
  onChange: (audioClipId: string | null) => void;
  defaultLabel: string;
  defaultAudioUrl: string;
}

export default function WaitAudioPicker({ value, onChange, defaultLabel, defaultAudioUrl }: WaitAudioPickerProps) {
  const { data: clips = [] } = useGetAudioClipsQuery();
  const [isLibraryOpen, setIsLibraryOpen] = useState(false);

  const selectedClip = clips.find((clip) => clip.id === value);
  const previewUrl = value ? selectedClip?.url : defaultAudioUrl;

  return (
    <div className="flex flex-wrap items-center gap-2 rounded-md border p-3">
      <FaMusic className="text-muted-foreground" />
      <span className="text-sm">{value ? (selectedClip?.name ?? "Selected clip") : defaultLabel}</span>
      <AudioPreviewButton src={previewUrl} />

      <div className="ml-auto flex gap-2">
        <Button type="button" variant="outline" size="sm" onClick={() => setIsLibraryOpen(true)}>
          {value ? "Change" : "Choose a clip"}
        </Button>
        {value && (
          <Button type="button" variant="ghost" size="sm" onClick={() => onChange(null)}>
            Use default
          </Button>
        )}
      </div>

      <Dialog open={isLibraryOpen} onOpenChange={setIsLibraryOpen}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>Choose wait audio</DialogTitle>
            <DialogDescription>
              Pick a clip from your library, or upload and record one here. Long clips loop until the wait ends.
            </DialogDescription>
          </DialogHeader>

          <div className="flex flex-wrap items-center gap-2 rounded-md border p-3">
            <FaMusic className="text-muted-foreground" />
            <span className="text-sm">{defaultLabel}</span>
            <AudioPreviewButton src={defaultAudioUrl} />
            {value && (
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="ml-auto"
                onClick={() => {
                  onChange(null);
                  setIsLibraryOpen(false);
                }}
              >
                Use this
              </Button>
            )}
          </div>

          <AudioClipLibrary
            onSelect={(clip) => {
              onChange(clip.id ?? null);
              setIsLibraryOpen(false);
            }}
          />
        </DialogContent>
      </Dialog>
    </div>
  );
}
